import { useCallback, useEffect, useState, FunctionComponent, MouseEvent } from 'react'

import { isArray, isEmpty, debounce } from 'libraries/lodash'
import cx from 'classnames'
import CommentIcon from '@material-ui/icons/ChatOutlined'
import Badge from '@material-ui/core/Badge'
import SpeedDial from '@material-ui/lab/SpeedDial'
import SpeedDialIcon from '@material-ui/lab/SpeedDialIcon'
import SpeedDialAction from '@material-ui/lab/SpeedDialAction'
import EyeIcon from '@material-ui/icons/Visibility'
import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import { scrollTo } from 'front-end-core/helpers/document'

import { CommentControlsContainerProps } from 'redux/containers/components/accordion/CommentControlsContainer'
import { ContentHeaderProps } from './ContentHeader'
import { dismissPopover } from 'services/OverlayService'

// debounce used when either of CommentControls options has been selected
// so that scroll to 'comments' or 'content editor' waits until after page has
// expanded and scrolled to content header first

const CommentControls: FunctionComponent<CommentControlsContainerProps & Partial<ContentHeaderProps>> = ({
  id,
  activeId,
  expanded,
  handleOnClick,
  commentsId,
  pageId,
  comments,
  addComment = true,
  setActiveId,
  deviceType: { isMobile, isDesktop }
}) => {
  const [isActive, setIsActive] = useState(false)
  const [open, setOpen] = useState(false)
  const handleOpen = useCallback(() => {
    setOpen(true)
    setActiveId(id)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    setActiveId(null)
  }, [])

  useEffect(() => {
    setIsActive(id === activeId)
  }, [id, activeId, setIsActive])

  const handleScroll = useCallback((e: MouseEvent<HTMLElement>) => {
    setActiveId(null)
    !expanded && handleOnClick(e)

    debounce(() => {
      scrollTo({ pageId, id: commentsId, animate: true, useWindowScrollTo: isMobile })
    }, 50)()
  }, [expanded, pageId, commentsId, isMobile, handleOnClick])

  const handleNewComment = useCallback((e: MouseEvent<HTMLElement>) => {
    setActiveId(null)
    !expanded && handleOnClick(e)
    const btnElement = document.getElementById(commentsId + '/new' + '/button')
    btnElement && btnElement.click()

    debounce(() => {
      scrollTo({ pageId, id: `${commentsId}/new`, animate: true, useWindowScrollTo: isMobile })
    }, 50)()
  }, [isMobile, pageId, commentsId, expanded, handleOnClick])

  if (!isArray(comments)) {
    return null
  }

  const activeComments = comments.filter(c => c.active)

  if (!activeComments.length && !addComment) {
    return null
  }

  return (
    <div className={cx({ 'transition-fade-out-slow': activeId && id !== activeId })}>
      <div className={cx('comment-controls relative mb8')}>
        <div
          className={cx('absolute right0 top0')}
          onClick={e => {
            dismissPopover(e.target)
            e.stopPropagation()
          }}
        >
          <Badge
            badgeContent={activeComments.length}
            max={9}
            classes={{
              badge: 'b absolute right2 top2 z1055',
              colorPrimary: cx('bg-teal-darken-10')
            }}
            color='primary'
          >
            <SpeedDial
              ariaLabel='SpeedDial'
              icon={
                <SpeedDialIcon
                  className='mt2 relative'
                  icon={<CommentIcon style={{ width: '1.25rem', height: '1.25rem' }} />}
                  openIcon={<CloseIcon style={{ width: '1.25rem', height: '1.25rem' }} />}
                />
              }
              onClose={handleClose}
              onOpen={handleOpen}
              open={open}
              direction={isDesktop ? 'left' : 'down'}
              FabProps={{
                size: 'small',
                classes: { sizeSmall: cx('mini-speed-dial', { mb3: isDesktop }) }
              }}
              className={cx({ z25: isActive })}
              classes={{
                fab: cx('hover-bg-teal bg-teal white outline-0 transition-all', { 'o-50 transition-all': !isActive }),
                actions: 'pt0'
              }}
            >
              {!isEmpty(activeComments) && (
                <SpeedDialAction
                  title='View comments'
                  icon={<EyeIcon style={{ width: '1.25rem', height: '1.25rem' }} />}
                  FabProps={{
                    size: 'small',
                    classes: {
                      sizeSmall: cx('mini-speed-dial', { 'mh0 mt0': !isDesktop }, { 'mh2 mt0': isDesktop })
                    }
                  }}
                  onClick={handleScroll}
                  tooltipPlacement={isDesktop ? 'top' : 'left'}
                  classes={{ fab: 'hover-bg-teal hover-white outline-0' }}
                  TooltipClasses={{ tooltip: 'bg-teal' }}
                />
              )}
              <SpeedDialAction
                title='Add comment'
                icon={<AddIcon style={{ width: '1.25rem', height: '1.25rem' }} />}
                FabProps={{
                  size: 'small',
                  classes: {
                    sizeSmall: cx('mini-speed-dial', { 'mh0 mt0': !isDesktop }, { 'mh2 mt0': isDesktop })
                  }
                }}
                onClick={handleNewComment}
                tooltipPlacement={isDesktop ? 'top' : 'left'}
                classes={{ fab: 'hover-bg-teal hover-white outline-0' }}
                TooltipClasses={{ tooltip: 'bg-teal' }}
              />
            </SpeedDial>
          </Badge>
        </div>
      </div>
    </div>
  )
}

export default CommentControls
